import { User, UserManager } from 'oidc-client-ts';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/store';
import { selectOidcConfig, setOidcConfig } from '../authSlice';
import { OidcConfig } from '../types';

/**
 * Custom hook to get the OIDC configuration from Redux state
 * @returns The OIDC configuration object or null if not available
 */
export const useSelectOidcConfig = (): OidcConfig | null => useSelector(selectOidcConfig);

// SSO Login Flow steps:
// Step 1 - continued - userManager.signinRedirect() is called to initiate the redirect to the SSO provider
// Step 2 - user authenticates on the SSO provider and is redirected back to the app with a code and state
// Step 3 - handleOIDCCallback is called when user returns to our app with a code
// Step 4 - userManager.signinRedirectCallback() returns a user object which
// contains the access token and refresh token
// Step 5 - we set the access token and refresh token in the store using loginActionDispatch
// and navigate to the home page

/**
 * React hook for managing OIDC authentication
 */
export function useOidcClient() {
  const appDispatch = useAppDispatch();
  const storedOidcConfig = useSelectOidcConfig();

  /**
   * Detects if the current URL contains OAuth callback parameters
   * @returns Boolean indicating if auth code is present in URL
   */
  const hasAuthCode = React.useMemo(() => window.location.search.includes('code='), []);

  /**
   * Handles the OIDC callback after user is redirected back from identity provider
   */
  const handleOIDCCallback = useCallback(async (): Promise<User> => {
    // Retrieve the stored response from localStorage
    if (!storedOidcConfig?.idpConfig) {
      // Handle the case where the response is missing or invalid
      throw new Error('OIDC configuration is not available');
    }

    // Update userManager settings
    const userManager = new UserManager({
      authority: storedOidcConfig.idpConfig.openidConfigUrl,
      client_id: storedOidcConfig.idpConfig.clientId,
      redirect_uri: window.location.origin + window.location.pathname,
      post_logout_redirect_uri: window.location.origin + window.location.pathname,
      response_type: 'code',
      scope: 'openid profile email offline_access',
      automaticSilentRenew: true,
      loadUserInfo: false,
      extraQueryParams: {
        tenantId: storedOidcConfig.idpConfig.otherParams.tenantId,
        idp_hint: storedOidcConfig.idpConfig.otherParams.idp_hint,
      },
    });

    // Check if userManager settings are valid
    if (!userManager.settings.authority) {
      // Handle the case where userManager settings are invalid
      throw new Error('Invalid OIDC configuration');
    }
    // Get the user object from the callback
    // Let the library extract parameters from the current URL
    const user = await userManager.signinRedirectCallback();
    if (!user) {
      // Handle the case where the user object is missing
      throw new Error('User object is missing after OIDC callback');
    }

    // Call the success callback with the user object
    return user;
  }, [storedOidcConfig]);

  // Function to initiate sign-in redirect
  /**
   * Initiates the OIDC sign-in process
   * @param config - The OIDC configuration
   * @param type - The type of authentication flow ('login' or 'signup')
   * @param state - Optional state parameters to pass to the identity provider
   * this is useful for passing additional data to the identity provider and
   * can be accessed after app comes back from the provider
   */
  const handleOIDCSignIn = useCallback(
    async (config: OidcConfig, state?: Record<string, any>) => {
      try {
        // Update settings with the config to create userManager
        const userManager = new UserManager({
          authority: config.idpConfig.openidConfigUrl,
          client_id: config.idpConfig.clientId,
          redirect_uri: window.location.origin + window.location.pathname,
          post_logout_redirect_uri: window.location.origin + window.location.pathname,
          scope: 'openid profile email offline_access',
          automaticSilentRenew: true,
          loadUserInfo: false,
          response_type: 'code',
          extraQueryParams: {
            tenantId: config.idpConfig.otherParams.tenantId,
            idp_hint: config.idpConfig.otherParams.idp_hint,
          },
        });

        // Store the config in Redux
        appDispatch(setOidcConfig(config));

        // if state value are provided, they will be passed as additional data to the identity provider
        // Redirect to identity provider
        await userManager.signinRedirect(state ? { state } : undefined);
      } catch (error) {
        console.error(`Error during ${window.location.href} redirect:`, error);
        throw error;
      }
    },
    [appDispatch],
  );

  return {
    handleOIDCCallback,
    handleOIDCSignIn,
    hasAuthCode,
  };
}
